/** All dashboard clocks: Asia/Kuala_Lumpur (Malaysia, UTC+08, no DST). */
const MY_TZ = "Asia/Kuala_Lumpur";
const MY_OFFSET_HINT = "(UTC+08:00)";

export function fmtTs(v) {
  if (!v) return "—";
  const t = typeof v === "number" ? (v > 1e12 ? v : v * 1000) : Date.parse(v);
  if (!Number.isFinite(t)) return String(v);
  const d = new Date(t);
  const base = new Intl.DateTimeFormat("en-CA", {
    timeZone: MY_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(d).replace(",", "");
  return `${base} ${MY_OFFSET_HINT}`;
}

export function fmtRel(v) {
  if (!v) return "—";
  const t = Date.parse(v);
  if (!Number.isFinite(t)) return String(v);
  const diff = Date.now() - t;
  if (diff < 60_000) return "just now";
  if (diff < 3600_000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400_000) return `${Math.floor(diff / 3600000)}h ago`;
  return `${Math.floor(diff / 86400000)}d ago`;
}

/** Soften zone keys like ``all`` / ``_all`` for operator UI. Empty = hide. */
export function humanizeZoneLabel(z) {
  const s = String(z || "").trim();
  if (!s || /^_?all$/i.test(s) || /^default$/i.test(s) || /^site$/i.test(s)) return "";
  return s;
}

/** Seconds → short human span (for offline age, SLA remaining). */
export function softDurationSeconds(secs) {
  const n = Number(secs);
  if (!Number.isFinite(n) || n < 0) return "";
  if (n < 60) return `${Math.round(n)}s`;
  if (n < 3600) return `${Math.floor(n / 60)}m`;
  if (n < 86400) return `${Math.floor(n / 3600)}h`;
  return `${Math.floor(n / 86400)}d`;
}

/** Proof-of-response / closure reason codes → plain language. */
export function humanizeProofReason(reason) {
  const s = String(reason || "").trim();
  if (!s) return "—";
  const map = {
    pending: "Waiting for confirmation",
    event_recorded_no_dispatch: "Alarm recorded — no responder assigned yet",
    dispatch_created: "Responder assigned",
    dispatch_resolved: "Responder marked resolved",
    verified: "Verified",
    override_close: "Closed by override",
    breached: "Past target time",
    met: "On time",
    open: "Open",
  };
  if (map[s]) return map[s];
  if (/^[a-z0-9_.]+$/i.test(s)) {
    return s.replace(/[._]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return s;
}

export function maskPlatform(_raw) {
  return "e**********";
}

/** Operator-facing alarm / event title — never raw snake_case wire types. */
export function humanizeAlarmTitle(raw) {
  const s = String(raw || "").trim();
  if (!s) return "Alarm";

  const offlineMatch = s.match(/\boffline\s*\((\d+)\s*s\)/i) || (/\boffline\b/i.test(s) ? ["offline"] : null);
  if (offlineMatch) {
    const age = offlineMatch[1] != null ? softDurationSeconds(offlineMatch[1]) : "";
    return age ? `Device offline · ${age}` : "Device offline";
  }

  if (/alarm\.fanout|\bfanout\b/i.test(s)) {
    const head = String(s.split("·")[0] || "")
      .trim()
      .replace(/[\[\]]+/g, " ")
      .replace(/\s{2,}/g, " ")
      .trim();
    const siteTok = head.split(/\s+/).find((t) => t && !/^SN-/i.test(t) && !/^device:/i.test(t)) || "";
    return siteTok ? `Group alarm · ${siteTok}` : "Group alarm";
  }

  const map = {
    remote_loud_button: "Remote loud alarm",
    remote_pause_button: "Remote pause",
    remote_silent_button: "Remote silent",
    remote_button: "Remote alarm",
    panic_button: "Panic alarm",
    panic_cancel_double_tap: "Panic cancel",
    mc_sensor_open: "Door / contact open",
    mc_sensor_closed: "Door / contact closed",
    network: "Network alarm",
    group_link: "Group linked alarm",
    siren_on: "Siren on",
    siren_off: "Siren off",
  };
  const paren = s.match(/\(([^)]+)\)\s*$/);
  const triggerKey = String(paren ? paren[1] : s.replace(/^alarm\./i, ""))
    .trim()
    .toLowerCase();
  const siteMatch = s.match(/\]\s*([^·]+?)(?:\s*·|\s*$)/);
  const site = siteMatch ? String(siteMatch[1] || "").trim() : "";
  let base = map[triggerKey];
  if (!base) {
    const key = triggerKey.replace(/^alarm\./, "");
    if (map[key]) base = map[key];
    else if (/^[a-z0-9_.]+$/i.test(triggerKey) && (triggerKey.includes("_") || triggerKey.includes("."))) {
      base = triggerKey
        .replace(/^alarm\./i, "")
        .replace(/[._]+/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
    } else if (!paren && !/^[a-z0-9_.]+$/i.test(s)) {
      // Strip device:SN-… / trailing actor tokens from operator summaries.
      let cleaned = s
        .replace(/device:\s*SN-[A-Z0-9]+/gi, "")
        .replace(/\balarm\.[a-z0-9_.]+/gi, "")
        .replace(/[\[\]]+/g, " ")
        .replace(/\s{2,}/g, " ")
        .replace(/^\[[^\]]+\]\s*/, "")
        .replace(/\s*\([^)]+\)\s*$/, "")
        .trim();
      const parts = cleaned.split("·").map((p) => p.trim()).filter(Boolean);
      if (parts.length >= 2 && /^SN-/i.test(parts[1])) {
        cleaned = parts[0];
      } else if (parts.length) {
        // Drop trailing bare username-looking token if present after site.
        cleaned = parts.filter((p, i) => i === 0 || !/^[a-z][a-z0-9_-]{2,31}$/i.test(p) || / /.test(p)).join(" · ");
      }
      base = cleaned || "Alarm";
    } else {
      base = map[key] || key.replace(/[._]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) || "Alarm";
    }
  }
  if (site && !base.toLowerCase().includes(site.toLowerCase())) {
    return `${base} · ${site}`;
  }
  return String(base || "Alarm")
    .replace(/\bSN-[A-Z0-9]{6,}\b/gi, "")
    .replace(/\s*[·|]\s*$/g, "")
    .replace(/^\s*[·|]\s*/g, "")
    .replace(/\s{2,}/g, " ")
    .trim() || "Alarm";
}

/** Audit log: prefix of action before first "." (for styling). */
export function auditActionPrefix(action) {
  const s = String(action || "").trim();
  const i = s.indexOf(".");
  const head = i > 0 ? s.slice(0, i) : (s || "other");
  // Server still emits bulk.siren_* — UI maps to "fleet" for chip styling.
  return head === "bulk" ? "fleet" : head;
}

/** UI label for audit.action / event.event_type (wire format may still use bulk.* / *.bulk_*). */
export function auditActionDisplay(action) {
  let s = String(action || "").trim();
  if (s.startsWith("bulk.")) {
    const rest = s.slice(5);
    return `All boards · ${rest}`;
  }
  s = s.replace(/\.bulk_/g, ".selected_devices_");
  if (s === "device.selected_devices_profile") return "Selected boards · profile";
  return s;
}

/** Strip detail fields that duplicate the row's actor/target columns. */
export function auditDetailDedupedRows(detail, actor, target) {
  if (!detail || typeof detail !== "object" || Array.isArray(detail)) return [];
  const a = String(actor || "").trim();
  const t = String(target || "").trim();
  const rows = [];
  for (const [k, raw] of Object.entries(detail)) {
    if (raw == null || raw === "") continue;
    const str = typeof raw === "object"
      ? (Array.isArray(raw) ? `${raw.length} items` : "Details on file")
      : String(raw);
    if (!str.trim()) continue;
    if (str === a && /^(actor|username|user|owner|owner_admin|created_by)$/i.test(k)) continue;
    if (t && str === t && /^(target|device_id|deviceId|source_id)$/i.test(k)) continue;
    let display = str;
    if (display.length > 220) display = `${display.slice(0, 217)}…`;
    rows.push({ k, v: display });
  }
  return rows;
}

/** Event detail: skip keys that duplicate the row (actor, target, device, owner). */
export function eventDetailDedupedRows(detail, e) {
  if (!detail || typeof detail !== "object" || Array.isArray(detail)) return [];
  const actor = String((e && e.actor) || "").trim();
  const target = String((e && e.target) || "").trim();
  const dev = String((e && e.device_id) || "").trim();
  const owner = String((e && e.owner_admin) || "").trim();
  const rows = [];
  for (const [k, raw] of Object.entries(detail)) {
    if (raw == null || raw === "") continue;
    const str = typeof raw === "object"
      ? (Array.isArray(raw) ? `${raw.length} items` : "Details on file")
      : String(raw);
    if (!str.trim()) continue;
    if (str === actor && /^(actor|username|user|owner_admin|created_by)$/i.test(k)) continue;
    if (target && str === target && /^(target)$/i.test(k)) continue;
    if (dev && str === dev && /^(device_id|deviceId|source_id|device)$/i.test(k)) continue;
    if (owner && str === owner && /^(owner_admin|owner)$/i.test(k)) continue;
    if (str === target && /^(target|device_id|deviceId)$/i.test(k)) continue;
    let display = str;
    if (display.length > 220) display = `${display.slice(0, 217)}…`;
    rows.push({ k, v: display });
  }
  return rows;
}

export function messagePayloadRows(payload) {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) return [];
  /** Tip 501: Soft operator labels for wire keys (never dump snake_case as-is). */
  const softKey = (raw) => {
    const k = String(raw || "").trim();
    const map = {
      ext_user_id: "Person id",
      user_id: "Person",
      device_id: "Device",
      alarm_id: "Alarm",
      alarm_lat: "Map · north",
      alarm_lng: "Map · east",
      latitude: "Map · north",
      longitude: "Map · east",
      lat: "Map · north",
      lng: "Map · east",
      map_lat: "Map · north",
      map_lng: "Map · east",
      risk_score: "Priority",
      risk: "Priority",
      event_id: "Case",
      event_type: "Alarm type",
      trigger_origin: "Trigger",
      classification: "Category",
      category: "Category",
    };
    // Preserve the legacy wire label without shipping a secret-like key literal.
    if (k.replace("_", "") === "cmdkey") return "Command";
    if (map[k] || map[k.toLowerCase()]) return map[k] || map[k.toLowerCase()];
    return k
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };
  const rows = [];
  for (const [k, raw] of Object.entries(payload)) {
    if (raw == null || raw === "") continue;
    if (String(k || "").startsWith("_")) continue;
    if (/^(ts|timestamp|nonce|seq|message_id|msg_id)$/i.test(String(k || ""))) continue;
    const str = typeof raw === "object"
      ? (Array.isArray(raw) ? `${raw.length} items` : "Details on file")
      : String(raw);
    if (!str.trim()) continue;
    let display = softOperatorNote(str) || str;
    if (display.length > 200) display = `${display.slice(0, 197)}…`;
    rows.push({ k: softKey(k), v: display });
  }
  return rows;
}

export function auditChipClass(action) {
  const p = auditActionPrefix(action);
  const map = {
    alarm: "audit-pfx-alarm",
    provision: "audit-pfx-prov",
    factory: "audit-pfx-factory",
    telegram: "audit-pfx-tg",
    auth: "audit-pfx-auth",
    admin: "audit-pfx-admin",
    user: "audit-pfx-user",
    command: "audit-pfx-cmd",
    mqtt: "audit-pfx-sys",
    device: "audit-pfx-dev",
    ota: "audit-pfx-ota",
    fleet: "audit-pfx-cmd",
    remote: "audit-pfx-alarm",
    signal: "audit-pfx-alarm",
    schedule: "audit-pfx-sys",
    login: "audit-pfx-auth",
    signup: "audit-pfx-auth",
  };
  return map[p] || "audit-pfx-other";
}

/**
 * Operator-facing device id — never show raw SN- / long opaque ids as primary copy.
 * Prefer display_label upstream; this is the last-resort softener.
 * @param {unknown} raw
 */
/**
 * True for soak / load / smoke harness ids — hide from primary operator lists when possible.
 * @param {unknown} raw
 */
export function isHarnessDeviceId(raw) {
  const s = String(raw || "").trim();
  if (!s) return false;
  return /smoke|soak|dry[_-]?run|load-r\d+|^r\d+-|w4c\d*_|^__.*__$/i.test(s);
}

/**
 * True for soak / smoke / policy-test usernames — hide from operator pickers.
 * @param {unknown} raw
 */
export function isHarnessUsername(raw) {
  const s = String(raw || "").trim();
  if (!s) return false;
  return /smoke|soak|dry[_-]?run|w4c\d*|policy-smoke|^__.*__$|fuckyou/i.test(s);
}

/** Last-known friendly names for device ids (seeded from overview / fleet lists). */
const _deviceLabelById = new Map();

/**
 * Remember operator-facing names so softDeviceLabel can resolve "Home" instead of Unit · SYV4…
 * @param {Iterable<object|string>|Record<string, string>|null|undefined} listOrMap
 */
export function rememberDeviceLabels(listOrMap) {
  if (!listOrMap) return;
  if (typeof listOrMap === "object" && !Array.isArray(listOrMap) && typeof listOrMap[Symbol.iterator] !== "function") {
    for (const [id, lab] of Object.entries(listOrMap)) {
      const k = String(id || "").trim();
      const v = String(lab || "").trim();
      if (!k || !v || /^SN-/i.test(v) || isHarnessDeviceId(k) || isHarnessDeviceId(v)) continue;
      if (/^site$/i.test(v) || /^_?all$/i.test(v)) continue;
      _deviceLabelById.set(k, v);
    }
    return;
  }
  try {
    for (const row of listOrMap) {
      if (!row) continue;
      if (typeof row === "string") continue;
      const id = String(row.device_id || row.id || "").trim();
      if (!id || isHarnessDeviceId(id)) continue;
      const label = String(row.display_label || row.device_label || "").trim();
      if (
        label
        && !/^site$/i.test(label)
        && !/^SN-/i.test(label)
        && !isHarnessDeviceId(label)
      ) {
        _deviceLabelById.set(id, label);
        continue;
      }
      const loc = String(row.location_label || "").trim();
      if (
        loc
        && !/^site$/i.test(loc)
        && !/^_?all$/i.test(loc)
        && !/^SN-/i.test(loc)
        && !isHarnessDeviceId(loc)
      ) {
        _deviceLabelById.set(id, loc);
      }
    }
  } catch (_) {}
}

export function softDeviceLabel(raw) {
  const s = String(raw || "").trim();
  if (!s || s === "—" || /^(unknown|n\/?a|null|undefined)$/i.test(s)) return "—";
  const remembered = _deviceLabelById.get(s);
  if (remembered) return remembered;
  const loadN = s.match(/^load-r\d+-(\d+)$/i);
  if (loadN) return `Practice unit · ${loadN[1]}`;
  if (/smoke|soak|dry[_-]?run/i.test(s)) return "Practice unit";
  if (/^SN-/i.test(s) && s.length > 16) {
    // Prefer mid body (often unique) + tail — avoid SN- prefix / common seed suffixes.
    const body = s.slice(3);
    return `Unit · ${body.slice(4, 8)}…${body.slice(-4)}`;
  }
  if (/^SN-/i.test(s)) {
    return `Unit · …${s.slice(-4).toUpperCase()}`;
  }
  if (s.length > 18 && /^[A-Za-z0-9_-]+$/.test(s)) {
    return `Unit · ${s.slice(0, 4)}…${s.slice(-4)}`;
  }
  return s.length <= 28 ? s : `${s.slice(0, 24)}…`;
}

/**
 * Operator-facing incident row for pickers (no raw event ids as the primary label).
 * @param {object|null|undefined} it
 */
export function softIncidentPickerLabel(it) {
  const row = it && typeof it === "object" ? it : {};
  let title = humanizeAlarmTitle(row.summary || row.event_type || row.title || "");
  title = String(title || "")
    .replace(/\bSN-[A-Z0-9]{6,}\b/gi, "")
    .replace(/\bdevice:\s*[A-Za-z0-9_-]+/gi, "")
    .replace(/\bby\s+[a-z0-9._-]{2,40}\b/gi, "")
    // Strip leading tenant / account tokens ("croc2026 Home · …" → "Home · …")
    .replace(/^[a-z][a-z0-9._-]{2,24}\s+(?=[A-Z])/i, "")
    .replace(/\bbulk\s+siren\s+off\s+(\d+)\s*device\(s\)/gi, "Mute all sirens · $1 units")
    .replace(/\bbulk\s+siren\s+on\s+(\d+)\s*device\(s\)/gi, "Sound all sirens · $1 units")
    .replace(/\bBulk siren off\b/gi, "Mute all sirens")
    .replace(/\bBulk siren on\b/gi, "Sound all sirens")
    .replace(/\bgroup settings applied for\s+[a-z0-9._-]+/gi, "Group settings applied")
    .replace(/\s*[·|]\s*[·|]\s*/g, " · ")
    .replace(/\s{2,}/g, " ")
    .replace(/\s*[·|]\s*$/g, "")
    .replace(/^\s*[·|]\s*/g, "")
    .trim();
  const site = String(row.group_name || row.site_name || row.notification_group || "").trim();
  const device = softDeviceLabel(
    row.display_label || row.device_label || row.friendly_name || row.device_id || row.device || "",
  );
  const when = row.ts != null || row.created_at != null
    ? fmtRel(row.ts != null ? row.ts : row.created_at)
    : "";
  const parts = [];
  if (title && title !== "—" && title !== "Alarm") parts.push(title);
  else if (site) parts.push(site);
  else parts.push("Recent alarm");
  if (device && device !== "—" && !/^Unit ·/i.test(device)) parts.push(device);
  else if (site && !parts.includes(site)) parts.push(site);
  if (when && when !== "—") parts.push(when);
  return parts.join(" · ");
}

/**
 * Fleet / asset row title — prefer human names over serials.
 * @param {object|null|undefined} d
 */
export function softFleetDeviceTitle(d) {
  if (!d || typeof d !== "object") return softDeviceLabel(d);
  const id = String(d.device_id || "").trim();
  if (isHarnessDeviceId(id)) return softDeviceLabel(id);
  const label = String(d.display_label || d.device_label || "").trim();
  if (
    label
    && !/^site$/i.test(label)
    && !/^SN-/i.test(label)
    && !isHarnessDeviceId(label)
  ) {
    if (id) _deviceLabelById.set(id, label);
    return label;
  }
  const loc = String(d.location_label || "").trim();
  if (
    loc
    && !/^site$/i.test(loc)
    && !/^_?all$/i.test(loc)
    && !/^SN-/i.test(loc)
    && !isHarnessDeviceId(loc)
  ) {
    if (id) _deviceLabelById.set(id, loc);
    return loc;
  }
  // Never use group/zone as the device title — that collapses distinct units into one label.
  return softDeviceLabel(id);
}

/**
 * Firmware chip text — version only, no board/vendor suffixes.
 * @param {unknown} fw
 */
export function softFirmwareChip(fw) {
  let s = String(fw || "").trim();
  if (!s) return "";
  s = s.replace(/^v/i, "");
  if (/probe|soak|smoke|dry[_-]?run|load-r/i.test(s)) return "Practice firmware";
  const m = s.match(/^(\d+(?:\.\d+){1,3})/);
  if (m) return `v${m[1]}`;
  if (s.length > 16) return `v${s.slice(0, 12)}…`;
  return `v${s}`;
}

/**
 * Operator-facing device count label (correct singular/plural).
 * @param {unknown} n
 * @returns {string}
 */
export function fmtDeviceCount(n) {
  const c = Math.max(0, Number(n) || 0);
  if (c === 0) return "no devices";
  if (c === 1) return "1 device";
  return `${c} devices`;
}

/**
 * @param {unknown} online
 * @param {unknown} total
 * @returns {string}
 */
export function fmtDevicesOnlineOfTotal(online, total) {
  const o = Math.max(0, Number(online) || 0);
  const t = Math.max(0, Number(total) || 0);
  // Capacity / catalog dumps (e.g. "2 of 10016") crush Live hero tiles — show online only.
  if (t >= 1000 && t / Math.max(o, 1) > 40) {
    return String(o);
  }
  const noun = t === 1 ? "device" : "devices";
  return `${o} of ${t} ${noun}`;
}

/**
 * Operator-facing API / network errors — never raw HTTP status lines or stack jargon.
 * @param {unknown} err
 * @param {string} [fallback]
 */
export function softApiError(err, fallback = "Something went wrong — try again in a moment.") {
  const msg = String((err && err.message) || err || "").trim();
  if (!msg) return fallback;
  const lower = msg.toLowerCase();
  if (/401|unauthorized|not authenticated|session/i.test(msg)) {
    if (/ota unlock|unlock credentials|verify-ota/i.test(msg)) {
      return "Unlock credentials are incorrect.";
    }
    return "Session expired — please sign in again.";
  }
  // Activation / verify flows before generic 403 — login returns 403 with activation detail.
  if (/account not activated|not activated yet/i.test(msg)) {
    return "Account not activated yet — open Activate account and finish email + phone.";
  }
  if (/phone not verified/i.test(msg)) {
    return "Confirm your phone first — tap Call my phone, answer, then Activate.";
  }
  if (/invalid or expired email code/i.test(msg)) {
    return "Email code is wrong or expired — request a new one and try again.";
  }
  if (/csrf|x-csrf-token|invalid csrf|csrf.?token|missing csrf/i.test(lower)) {
    return "Session needs a refresh — reload the page and try again.";
  }
  if (/403|forbidden|not allowed|capability/i.test(msg)) {
    return "You do not have permission for this action.";
  }
  if (/404|not found/i.test(msg)) {
    return "That item was not found.";
  }
  if (/timed?\s*out|timeout|abort|page render timed out/i.test(lower)) {
    return "Request timed out — wait a moment and retry.";
  }
  if (/sqlite_physical_eol|sqlite_eol_enforce|sqlite_physical_eol_use_pg|physical_eol|store_ready/i.test(msg)) {
    return "This feature is finishing its storage upgrade — temporarily unavailable.";
  }
  if (/pg_required|primary database required|postgres required/i.test(msg)) {
    return "This feature is not opened for this account yet.";
  }
  if (/503|database contended|system busy|temporarily unavailable|account_store|account store|primary database|read-only on this deployment/i.test(msg)) {
    return "Server busy — wait a moment and retry.";
  }
  if (/429|rate.?limit|too many requests|tenant_rate_limited/i.test(msg)) {
    return "Too many requests — wait a few seconds and retry.";
  }
  if (/500|internal server error|bad gateway|502|504/i.test(msg)) {
    return "Service temporarily unavailable — try again shortly.";
  }
  if (/validation error|literal_error|type=literal|input should be/i.test(lower)) {
    return "That choice is not supported yet — pick another When / Then and try again.";
  }
  if (/unsupported_trigger_kind/i.test(lower)) {
    return "That When option is not supported — pick Sensor threshold, online/offline, or schedule.";
  }
  if (/network|failed to fetch|load failed|offline/i.test(lower)) {
    return "Network issue — check your connection and retry.";
  }
  // Strip leading status codes like "500: …" / "HTTP 500 …"
  const cleaned = msg
    .replace(/^(?:HTTP\s*)?\d{3}\s*[:\-–—]?\s*/i, "")
    .replace(/\binternal server error\b/gi, "")
    .trim();
  if (!cleaned || cleaned.length < 3) return fallback;
  // Still looks like wire jargon → fallback
  if (/^(chat_id|device_id|grantee|token|pid|flag)\b/i.test(cleaned)) return fallback;
  // Raw JSON / brace dumps from API detail — never show to operators
  if (/^[\[{]/.test(cleaned) || /"code"\s*:/.test(cleaned)) return fallback;
  if (cleaned.length > 160) return `${cleaned.slice(0, 157)}…`;
  return cleaned;
}

/**
 * Soften ops/env/remediation strings for operator UI (no flags, no store jargon).
 * @param {unknown} raw
 * @returns {string}
 */
export function softOperatorNote(raw) {
  let s = String(raw || "").trim();
  if (!s) return "—";
  // Same sentences as CMC whySay — inbound gap codes only, no third map.
  if (/^no_alarm_location$/i.test(s)) return "We do not have a place for this alarm";
  if (/^no_fresh_responder$/i.test(s)) return "No one is on site yet";
  // Whole-line patterns first (backup / readiness remediation blobs)
  s = s
    .replace(
      /\bDB_BACKUP_ENABLED\s*=\s*0\b[\s\S]*?(?:PITR|recovery)(?:\s+is\s+(?:live|enabled))?\.?/gi,
      "Scheduled backup is off — use Settings manual backup until automated recovery is enabled",
    )
    .replace(
      /\bEnable\s+SIMULATION_ENABLED\s+to\s+run\s+isolated\s+response\s+drills\.?/gi,
      "Ask your platform admin to open practice drills so you can run isolated response drills.",
    )
    .replace(
      /\bEnable\s+HEALTH_ENGINE_ENABLED\s+for\s+operator\s+health\s+scoring\.?/gi,
      "Ask your platform admin to open device health scoring.",
    )
    .replace(
      /\bEnable\s+SOP_KNOWLEDGE_ENABLED\s+for\s+certified-plan\s+recommendations\.?/gi,
      "Ask your platform admin to open team notes for approved-plan recommendations.",
    );
  s = s
    .replace(/\bSIMULATION_ENABLED\b/gi, "practice drills")
    .replace(/\bHEALTH_ENGINE_ENABLED\b/gi, "device health scoring")
    .replace(/\bSOP_KNOWLEDGE_ENABLED\b/gi, "team notes")
    .replace(/\bDB_BACKUP_ENABLED\s*=\s*0\b/gi, "Scheduled backup is off")
    .replace(/\bDB_BACKUP_ENABLED\s*=\s*1\b/gi, "Scheduled backup is on")
    .replace(/\bEnable\s+practice drills\s+to\b/gi, "Ask your platform admin to open practice drills so you can")
    .replace(/\bEnable\s+device health scoring\s+for\b/gi, "Ask your platform admin to open device health scoring for")
    .replace(/\bEnable\s+fleet health scoring\s+for\b/gi, "Ask your platform admin to open device health scoring for")
    .replace(/\bEnable\s+playbook knowledge\s+for\b/gi, "Ask your platform admin to open team notes for")
    .replace(/\bSOP knowledge bridge\b/gi, "Team notes")
    .replace(/\bSOP knowledge\b/gi, "Team notes")
    .replace(/\bAsk platform ops to\b/gi, "Ask your platform admin to")
    .replace(/\bcertified-plan\b/gi, "sealed plan")
    .replace(/\bcertified plan\b/gi, "sealed plan")
    .replace(/\boperator notes\b/gi, "team notes")
    .replace(/\bOperator notes\b/g, "Team notes")
    .replace(/\bFleet health\b/gi, "Device health")
    .replace(/\bHealth engine\b/gi, "Device health")
    .replace(/\bfleet health scoring\b/gi, "device health scoring")
    .replace(/\bResponder dispatch module\b/gi, "Response team dispatch")
    .replace(/\bCamera evidence path\b/gi, "Camera evidence")
    .replace(/\bAI incident analysis\b/gi, "Advisory analysis")
    .replace(/\bFCM\b/gi, "push alerts")
    .replace(/\bMQTT\b/gi, "device link")
    .replace(/\bSSE\b/g, "live updates")
    .replace(/\bwebhook\b/gi, "callback")
    .replace(/\breplica\b/gi, "copy")
    .replace(/\bDDL\b/g, "schema change")
    .replace(/\bGate-\d+\b/g, "")
    .replace(/\bJSON\b/g, "data")
    .replace(/\bActuators?\b/gi, "equipment")
    .replace(/\bSwarm\b/gi, "response team")
    .replace(/\bdispatch rule\b/gi, "response plan")
    .replace(/\bSQLite\s*DR\b/gi, "legacy local disaster recovery")
    .replace(/\bSQLite\b/gi, "legacy local store")
    .replace(/\bPG\s*PITR\b/gi, "automated recovery")
    .replace(/\bCAP-OPS-\d+\b/gi, "the operations backlog item")
    .replace(/\bPostgres(?:ql)?\b|\bPG\b/g, "primary database")
    .replace(/\byour tenant\b/gi, "your account")
    .replace(/\btenant is response-ready\b/gi, "account is response-ready")
    .replace(/\bfor your tenant\b/gi, "for your account")
    .replace(/\bTenant owner\b/gi, "Account owner")
    .replace(/\bLoad tenant\b/gi, "Load account")
    .replace(/\btenant owner\b/gi, "account owner")
    .replace(/\bcross-tenant\b/gi, "cross-account")
    .replace(/\bdry-run\b/gi, "practice run")
    .replace(/\bSIMULATION_ENABLED is off on this server\./gi, "Practice drills are turned off on this deployment.")
    .replace(/\blegacy local disaster recovery relies on manual backup\b/gi, "use Settings manual backup")
    .replace(/\buntil primary-database recovery is (?:live|enabled)\b/gi, "until automated recovery is enabled")
    .replace(/\buntil the operations backlog item primary-database recovery is live\b/gi, "until automated recovery is enabled")
    .replace(/\bprimary-database point-in-time recovery\b/gi, "automated recovery")
    .replace(/\bintegrity_alerts\s*=\s*/gi, "Integrity alerts: ")
    .replace(/\bpg_active\b/gi, "Primary store active")
    .replace(/\bsqlite_stale\b/gi, "Legacy store stale")
    .replace(/\bdrift_total\b/gi, "Drift count")
    .replace(/\bmac_fp\b/gi, "Hardware fingerprint")
    .replace(/\bmanual\/ops backup\b/gi, "manual backup")
    .replace(/\bowner_admin required for superadmin\b/gi, "Choose an account owner first")
    .replace(/\b403\s*tenant user scopes are for tenant admins only\b/gi,
      "Team access is for account admins — sign in as an account admin to manage it")
    .replace(/\btenant user scopes are for tenant admins only\b/gi,
      "Team access is for account admins — sign in as an account admin to manage it")
    .replace(/\bOnly the parent tenant admin can use this page\b/gi,
      "Only the parent account admin can use this page")
    .replace(/\bAssign manager and tenant\b/gi, "Assign manager admin")
    .replace(/\bmanager and tenant\b/gi, "manager admin")
    .replace(/\bthis device\b/gi, "this board")
    .replace(/\bother devices\b/gi, "other boards")
    .replace(/\bthis tenant\b/gi, "this site")
    .replace(/\s*\(no_alarm_location\)\.?/gi, "")
    .replace(/\s*\(no_fresh_responder\)\.?/gi, "");
  // KEY=value env dumps
  if (/^[A-Z][A-Z0-9_]+=/.test(s)) {
    const eq = s.indexOf("=");
    const key = s.slice(0, eq).replace(/_/g, " ").toLowerCase();
    const val = s.slice(eq + 1).trim();
    s = `${key}: ${val}`;
  }
  // "Enable FOO to …" leftover flag-shaped tokens
  s = s.replace(/\bEnable\s+([A-Z][A-Z0-9_]{3,})\s+to\b/g, "Ask your platform admin to open this capability so you can");
  return s;
}

/**
 * Soften a single ops / system log line for operator UI (no stack dumps, no flag names).
 * @param {unknown} line
 * @returns {string}
 */
export function softOpsLogLine(line) {
  let s = String(line || "").trim();
  if (!s) return "";
  if (/^[\[{]/.test(s) || /^\s*at\s+\S+/i.test(s) || /Traceback \(most recent/i.test(s)) {
    return "";
  }
  s = s.replace(/^\[?(\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}(?:[.,]\d+)?Z?)\]?\s*/i, "");
  s = softOperatorNote(s);
  if (!s || s === "—") return "";
  if (s.length > 180) s = `${s.slice(0, 177)}…`;
  return s;
}
